/*---------------------------------------------------------------------------------------------
 *  Copyright (c) i-Bridge Systems Philippines Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/
import {
  TableHead,
  TableRow,
  TableBody,
  IconButton,
  Grid,
  MenuItem,
  Tooltip,
  Box,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useEffect, useRef, useState } from 'react';
import { apiUrl } from '../../utilities/apiUrl';
import {
  QueryContainer,
  ContainedButton,
  OutlinedButton,
  SelectDropdown,
  Search,
  GeneralTable,
  CustomTableCell,
  ActionTableCell,
  CustomTableRow,
  ReminderModal,
  ConfirmationModal,
  SuccessModal,
} from '../index';

const entryPerPage = 10;

const TruncatedText = ({ text, maxWidth }) => {
  const textRef = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const el = textRef.current;
    if (el) {
      setIsTruncated(el.scrollWidth > el.clientWidth);
    }
  }, [text, maxWidth]);

  return (
    <Tooltip
      title={text}
      arrow
      placement="top-start"
      disableHoverListener={!isTruncated}
    >
      <Box
        ref={textRef}
        sx={{
          maxWidth,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {text}
      </Box>
    </Tooltip>
  );
};

export const Reminders = (props) => {
  const employeeList = props.employeeList || [];
  const [reminderList, setReminderList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [slice, setSlice] = useState([0, entryPerPage]);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [editingReminder, setEditingReminder] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [triggerFilter, setTriggerFilter] = useState('ALL');
  const [repeatFilter, setRepeatFilter] = useState('ALL');

  const fetchReminders = () => {
    fetch(apiUrl('/reminder'), {
      method: 'GET',
      credentials: 'include',
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          setReminderList(data.data);
        }
        setIsLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setIsLoading(false);
      });
  };

  useEffect(() => {
    fetchReminders();
  }, []);

  // FILTER REMINDERS BY SEARCH TEXT + STATUS/TRIGGER/REPEAT DROPDOWNS
  const filteredReminders = reminderList.filter((reminder) => {
    if (
      searchValue &&
      !reminder.title.toUpperCase().includes(searchValue.toUpperCase())
    ) {
      return false;
    }
    if (statusFilter === 'ACTIVE' && !reminder.active) return false;
    if (statusFilter === 'INACTIVE' && reminder.active) return false;
    if (triggerFilter !== 'ALL' && reminder.trigger !== triggerFilter) {
      return false;
    }
    if (repeatFilter !== 'ALL' && reminder.repeat !== repeatFilter) {
      return false;
    }
    return true;
  });

  // RESET PAGINATION TO PAGE 1; CALLED BY EACH FILTER'S onChange
  const resetPagination = () => {
    setCurrentPage(1);
    setSlice([0, entryPerPage]);
  };

  const pageCount = Math.floor(filteredReminders.length / entryPerPage);

  // PAGE PAGINATION HANDLER
  const handlePageChange = (e, page) => {
    setCurrentPage(page);
    const currPage = page - 1;
    if (page === 1) {
      setSlice([0, entryPerPage]);
    } else {
      setSlice([currPage * entryPerPage, page * entryPerPage]);
    }
  };

  // RESET QUERY HANDLER
  const handleResetQuery = () => {
    setSearchValue('');
    setStatusFilter('ALL');
    setTriggerFilter('ALL');
    setRepeatFilter('ALL');
    resetPagination();
  };

  // ADD REMINDER MODAL HANDLER
  const handleAddModalOpen = () => {
    setEditingReminder(null);
    setFormModalOpen(true);
  };

  // EDIT REMINDER MODAL HANDLER
  const handleEditModalOpen = (reminder) => {
    setEditingReminder(reminder);
    setFormModalOpen(true);
  };

  // DELETE CONFIRMATION MODAL HANDLER
  const handleDeleteModalOpen = (reminder) => {
    setDeleteTarget(reminder);
    setDeleteConfirmOpen(true);
  };

  // REMINDER SAVED (CREATE OR EDIT) HANDLER
  const handleSaved = (message) => {
    setFormModalOpen(false);
    fetchReminders();
    setSuccessMessage(message);
    setSuccessModalOpen(true);
  };

  // DELETE REMINDER HANDLER
  const handleDelete = () => {
    fetch(apiUrl('/reminder/' + deleteTarget._id), {
      method: 'DELETE',
      credentials: 'include',
    })
      .then((response) => response.json())
      .then((data) => {
        setDeleteConfirmOpen(false);
        if (data.success) {
          fetchReminders();
          setSuccessMessage('Reminder deleted.');
          setSuccessModalOpen(true);
        } else {
          alert(data.message);
        }
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const triggerLabels = {
    AFTER_CHECKIN: 'After Check-in',
    BEFORE_CHECKOUT: 'Before Check-out',
    AFTER_CHECKOUT: 'After Check-out',
  };

  const describeTrigger = (reminder) =>
    triggerLabels[reminder.trigger] || reminder.trigger;

  const repeatLabels = {
    ONCE: 'Once',
    DAILY: 'Daily',
  };

  const describeRepeat = (reminder) =>
    repeatLabels[reminder.repeat] || reminder.repeat;

  const describeTarget = (reminder) => {
    if (reminder.targettype === 'ALL') return 'All Employees';
    const count = reminder.targetempids?.length || 0;
    return `${count} specific employee${count === 1 ? '' : 's'}`;
  };

  const renderLongText = (text, maxWidth) => {
    if (!text) return '-';
    return <TruncatedText text={text} maxWidth={maxWidth} />;
  };

  const describeUpdatedBy = (reminder) => {
    const updater = employeeList.find((emp) => emp._id === reminder.updatedby);
    return updater ? `${updater.firstname} ${updater.lastname}` : '-';
  };

  const describeSchedule = (reminder) => {
    const start = reminder.startdate
      ? new Date(reminder.startdate).toLocaleDateString()
      : null;
    const end = reminder.enddate
      ? new Date(reminder.enddate).toLocaleDateString()
      : null;

    if (!start && !end) return 'Always';
    if (start && end) return `${start} - ${end}`;
    if (start) return `From ${start}`;
    return `Until ${end}`;
  };

  return (
    <>
      <QueryContainer>
        <Grid item>
          <Grid container spacing={2} alignItems="center">
            <Grid item>
              <Search
                searchValue={searchValue}
                onChange={(event) => {
                  setSearchValue(event.target.value);
                  resetPagination();
                }}
                handleSearch={() => {}}
              />
            </Grid>
            <Grid item>
              <SelectDropdown
                sx={{ minWidth: 150 }}
                size="small"
                label="Status"
                value={statusFilter}
                onChange={(event) => {
                  setStatusFilter(event.target.value);
                  resetPagination();
                }}
              >
                <MenuItem value="ALL">All</MenuItem>
                <MenuItem value="ACTIVE">Active</MenuItem>
                <MenuItem value="INACTIVE">Inactive</MenuItem>
              </SelectDropdown>
            </Grid>
            <Grid item>
              <SelectDropdown
                sx={{ minWidth: 180 }}
                size="small"
                label="When"
                value={triggerFilter}
                onChange={(event) => {
                  setTriggerFilter(event.target.value);
                  resetPagination();
                }}
              >
                <MenuItem value="ALL">All</MenuItem>
                <MenuItem value="AFTER_CHECKIN">After Check-in</MenuItem>
                <MenuItem value="BEFORE_CHECKOUT">Before Check-out</MenuItem>
                <MenuItem value="AFTER_CHECKOUT">After Check-out</MenuItem>
              </SelectDropdown>
            </Grid>
            <Grid item>
              <SelectDropdown
                sx={{ minWidth: 150 }}
                size="small"
                label="Repeat"
                value={repeatFilter}
                onChange={(event) => {
                  setRepeatFilter(event.target.value);
                  resetPagination();
                }}
              >
                <MenuItem value="ALL">All</MenuItem>
                <MenuItem value="ONCE">Once</MenuItem>
                <MenuItem value="DAILY">Daily</MenuItem>
              </SelectDropdown>
            </Grid>
            <Grid item>
              <OutlinedButton btnTitle="Reset" onClick={handleResetQuery} />
            </Grid>
          </Grid>
        </Grid>
        <Grid item>
          <ContainedButton
            onClick={handleAddModalOpen}
            btnTitle="Add Reminder"
            startIcon={<AddIcon />}
            style={{ px: 1, width: 170 }}
          />
        </Grid>
      </QueryContainer>
      <GeneralTable
        isDataLoading={isLoading}
        noRecord={filteredReminders.length === 0}
        sx={{
          borderRadius: '5px',
          boxShadow: 'rgba(0, 0, 0, 0.16) 0px 1px 4px',
          mb: 3.6,
          display: filteredReminders.length === 0 ? 'none' : 'block',
        }}
        size="small"
        length={filteredReminders.length}
        entryPerPage={entryPerPage}
        page={currentPage}
        pageCount={pageCount}
        handlePageChange={handlePageChange}
      >
        <TableHead>
          <TableRow>
            <CustomTableCell value="Title" />
            <CustomTableCell align="left" value="When" />
            <CustomTableCell align="left" value="Repeat" />
            <CustomTableCell align="left" value="Shown To" />
            <CustomTableCell align="left" value="Schedule" />
            <CustomTableCell align="center" value="Status" />
            <CustomTableCell align="left" value="Last Updated By" />
            <CustomTableCell align="left" value="Notes" />
            <CustomTableCell align="center" value="Actions" />
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredReminders.slice(slice[0], slice[1]).map((reminder) => (
            <CustomTableRow key={reminder._id}>
              <CustomTableCell
                scope="row"
                value={renderLongText(reminder.title, 220)}
              />
              <CustomTableCell scope="row" value={describeTrigger(reminder)} />
              <CustomTableCell scope="row" value={describeRepeat(reminder)} />
              <CustomTableCell scope="row" value={describeTarget(reminder)} />
              <CustomTableCell scope="row" value={describeSchedule(reminder)} />
              <CustomTableCell
                align="center"
                scope="row"
                value={reminder.active ? 'Active' : 'Inactive'}
              />
              <CustomTableCell
                scope="row"
                value={describeUpdatedBy(reminder)}
              />
              <CustomTableCell
                scope="row"
                value={renderLongText(reminder.notes, 250)}
              />
              <ActionTableCell>
                <IconButton
                  size="small"
                  title="Edit"
                  onClick={() => handleEditModalOpen(reminder)}
                >
                  <EditIcon fontSize="inherit" />
                </IconButton>
                <IconButton
                  size="small"
                  title="Delete"
                  onClick={() => handleDeleteModalOpen(reminder)}
                >
                  <DeleteIcon fontSize="inherit" />
                </IconButton>
              </ActionTableCell>
            </CustomTableRow>
          ))}
        </TableBody>
      </GeneralTable>
      <ReminderModal
        open={formModalOpen}
        onClose={() => setFormModalOpen(false)}
        reminder={editingReminder}
        employeeList={employeeList}
        onSaved={handleSaved}
      />
      <ConfirmationModal
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        title="Delete Reminder"
        desc={`Are you sure you want to delete "${deleteTarget?.title}"?`}
        primaryBtnName="Delete"
        secondaryBtnName="Cancel"
        primaryClickHandler={handleDelete}
        secondaryClickHandler={() => setDeleteConfirmOpen(false)}
      />
      <SuccessModal
        open={successModalOpen}
        onClose={() => setSuccessModalOpen(false)}
        close={() => setSuccessModalOpen(false)}
        desc={successMessage}
      />
    </>
  );
};
